import { Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import { EAllowStatus } from '~/src/dash/apiV2/targeting/targetingPresets';
import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { IncludeExcludeToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/IncludeExcludeToggle';
import { SimpleSwitch } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/SimpleSwitch';
import { DaypartingScheduleBuilder } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/DaypartingScheduleBuilder';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import { Col, Cols, ISimpleOptionSelect, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const labelColWidth = 64;
const rowHeight = 40;

// DO NOT REUSE OR COPY/PASTE. THIS IS A CUSTOM SOLUTION COMPONENT
export const DaypartingBuilder = () => {
  const { session } = useBaseStore();
  const { object, defaultObject } = useFormBuilderStore();

  const { DaypartingUI } = object;

  const [isEnabled, setIsEnabled] = useState(DaypartingUI?.Enabled || false);
  const [status, setStatus] = useState<EAllowStatus>(object.DaypartingIsAllowed);
  const [isViewerTimezone, setIsViewerTimezone] = useState(
    DaypartingUI?.EnableViewerTimezone || false,
  );

  useEffect(() => {
    return () => {
      object.Dayparting = defaultObject.Dayparting;
      object.DaypartingIsAllowed = defaultObject.DaypartingIsAllowed;
      object.DaypartingUI = defaultObject.DaypartingUI;
      object.DaypartingUI.Timezone = session.publisher.Timezone;
    };
  }, []);

  const handleSwitchEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsEnabled(e.target.checked);
    DaypartingUI.Enabled = e.target.checked;
  };

  const handleToggleStatus = () => {
    const newStatus = status === EAllowStatus.ALLOW ? EAllowStatus.DENY : EAllowStatus.ALLOW;
    object.DaypartingIsAllowed = newStatus;
    setStatus(newStatus);
  };

  const handleSwitchTimezone = (value: string) => {
    const isViewer = value === 'viewer';
    setIsViewerTimezone(isViewer);
    DaypartingUI.EnableViewerTimezone = isViewer;
  };

  return (
    <Rows gap={spacing.extraSmall} smallHorizontalPadding>
      <Row noPadding height={rowHeight}>
        <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col width={labelColWidth}>
            <Text small>Active</Text>
          </Col>
          <Col>
            <SimpleSwitch
              isChecked={isEnabled}
              handleSwitch={handleSwitchEnabled}
              key='Dayparting'
              inputID='dayparting_enabled_toggle'
            />
          </Col>
          <Col>
            <Tooltip
              title='Use dayparting to set days of the week and hours when you want this campaign to run. Adding multiple options will provide extra granularity.'
              placement='top'
            >
              <div style={{ display: 'flex' }}>
                <IconV2 name='info-static' color={publicaColors.gray} />
              </div>
            </Tooltip>
          </Col>
        </Cols>
      </Row>
      {isEnabled && (
        <Row noPadding>
          <Rows>
            <Row noPadding height={rowHeight}>
              <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                <Col width={labelColWidth}>
                  <Text small>Logic</Text>
                </Col>
                <Col>
                  <IncludeExcludeToggle
                    status={status}
                    handleToggleStatus={handleToggleStatus}
                    key={'DaypartingIsAllowed'}
                  />
                </Col>
                <Col flex style={{ textAlign: 'right' }}>
                  <Text extraSmall italic>
                    {status === EAllowStatus.ALLOW
                      ? 'Campaign will deliver during the selected hours and days.'
                      : 'Campaign will NOT deliver during the selected hours and days.'}
                  </Text>
                </Col>
              </Cols>
            </Row>
            <Row noPadding height={rowHeight}>
              <Cols gap={spacing.base} style={{ alignItems: 'center', height: `${rowHeight}px` }}>
                <Col width={labelColWidth}>
                  <Text small>Timezone</Text>
                </Col>
                <Col>
                  <PillSwitcherV2
                    size='small'
                    options={
                      [
                        {
                          key: 'bidder',
                          label: 'Bidder',
                          value: 'bidder',
                          isSelected: !isViewerTimezone,
                        },
                        {
                          key: 'viewer',
                          label: 'Viewer',
                          value: 'viewer',
                          isSelected: isViewerTimezone,
                        },
                      ] as ISimpleOptionSelect[]
                    }
                    onChange={handleSwitchTimezone}
                  />
                </Col>
                <Col flex style={{ textAlign: 'right' }}>
                  <Text extraSmall italic>
                    {isViewerTimezone
                      ? `Dayparting will adhere to the viewer's timezone using their device location`
                      : `Dayparting will adhere to the timezone set for the bidder.`}
                  </Text>
                </Col>
              </Cols>
            </Row>
            <Row smallVerticalPadding noHorizontalPadding style={{ minHeight: `${rowHeight}px` }}>
              <Cols
                gap={spacing.base}
                style={{ alignItems: 'flex-start', minHeight: `${rowHeight}px` }}
              >
                <Col paddingTop={spacing.extraSmall} width={labelColWidth}>
                  <Text small>Period</Text>
                </Col>
                <Col flex>
                  <DaypartingScheduleBuilder initialPeriods={DaypartingUI.Periods} />
                </Col>
              </Cols>
            </Row>
          </Rows>
        </Row>
      )}
    </Rows>
  );
};
